import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import { InvestmentPropertyService } from '../../../../../modules/investment/property/investment-property.service';
import { InvestmentPropertyDepositsModel } from '../../../../../modules/investment/property/investment-property.model';
import { InvestmentPropertyDialogPayComponent } from '../../../../../modules/investment/property/investment-property-dialog-pay.component';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import {
  RealStateInvestmentRow,
  realStatePaidLabel,
  realStatePaidVariant,
  realStateUsageLabel,
  roundMoney,
} from '../../models/real-state-investment.model';

interface DepositLine {
  uid: string;
  date: Date | string;
  concept: string;
  meta: string;
  amount: number;
  deposit: InvestmentPropertyDepositsModel;
}

const MAX_LINES = 5;

@Component({
  selector: 'app-real-state-investment-detail',
  templateUrl: './real-state-investment-detail.component.html',
  styleUrls: ['./real-state-investment-detail.component.scss'],
  standalone: false,
})
export class RealStateInvestmentDetailComponent implements OnChanges {
  @Input() row!: RealStateInvestmentRow;
  @Input() version = 0;
  @Output() changed = new EventEmitter<void>();
  @Output() edit = new EventEmitter<void>();

  readonly maxLines = MAX_LINES;
  readonly year = ActualSelected.yearSelected;

  loading = true;
  error = false;
  lines: DepositLine[] = [];
  count = 0;
  paidYear = 0;
  commissions = 0;

  private loadedKey = '';

  constructor(
    private propertyService: InvestmentPropertyService,
    private dialog: MatDialog,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get usageLabel(): string {
    return realStateUsageLabel(this.row && this.row.usage);
  }

  get paidLabel(): string {
    return realStatePaidLabel(this.row);
  }

  get paidVariant() {
    return realStatePaidVariant(this.row);
  }

  get percent(): number {
    const total = Number(this.row && this.row.total) || 0;
    if (total <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((Number(this.row.totalPaid) || 0) / total * 100)));
  }

  get taxesLabel(): string {
    const taxes = (this.row && this.row.taxes) || [];
    return taxes.length ? taxes.map(tax => tax.name).join(', ') : 'Sin impuestos';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.row || !this.row.uid) return;
    const key = [this.row.uid, this.version].join('|');
    if (key === this.loadedKey) return;
    this.loadedKey = key;
    this.load(this.row.uid);
  }

  retry(): void {
    if (!this.row || !this.row.uid) return;
    this.load(this.row.uid);
  }

  add(): void {
    this.openPayDialog();
  }

  manage(): void {
    this.openPayDialog();
  }

  editLine(line: DepositLine): void {
    this.openPayDialog(line.uid);
  }

  deleteLine(line: DepositLine): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.confirm.open({
      title: 'Eliminar pago',
      subtitle: [this.row.investmentDescription, this.row.contactsName].filter(Boolean).join(' · ') || undefined,
      message: 'Se eliminará el pago y su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.details(line),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando pago…',
      errorText: 'No se pudo eliminar el pago',
      action: () => this.propertyService.depositDelete(line.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Pago eliminado correctamente');
      this.changed.emit();
    });
  }

  trackByUid(_: number, line: DepositLine): string {
    return line.uid;
  }

  private openPayDialog(editUid?: string): void {
    if (!this.tier.requireDocumentsTier()) return;
    InvestmentPropertyDialogPayComponent.open(this.dialog, { investment: this.row, editUid })
      .subscribe(changed => { if (changed) this.changed.emit(); });
  }

  private load(uid: string): void {
    this.loading = !this.lines.length;
    this.error = false;

    this.propertyService.depositList(uid)
      .then(list => {
        if (!this.row || uid !== this.row.uid) return;
        const deposits = (Array.isArray(list) ? list : [])
          .slice()
          .sort((a, b) => new Date(b.depositDate).getTime() - new Date(a.depositDate).getTime());
        const year = Number(this.year);
        this.count = deposits.length;
        this.paidYear = roundMoney(deposits
          .filter(item => new Date(item.depositDate).getFullYear() === year)
          .reduce((acc, item) => acc + (Number(item.amount) || 0), 0));
        this.commissions = roundMoney(deposits.reduce((acc, item) => acc + (Number(item.commissions) || 0), 0));
        this.lines = deposits.slice(0, MAX_LINES).map(item => ({
          uid: item.uid,
          date: item.depositDate,
          concept: item.typePay || 'Sin forma de pago',
          meta: [
            item.bankAccountInfo || '',
            Number(item.commissions) ? 'Comisión ' + Utils.FormatMoney(item.commissions) + ' €' : '',
          ].filter(Boolean).join(' · '),
          amount: Number(item.amount) || 0,
          deposit: item,
        }));
      })
      .catch(() => { this.error = true; })
      .then(() => { this.loading = false; });
  }

  private details(line: DepositLine): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (line.date) details.push({ label: 'Fecha', value: formatDate(line.date, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Forma de pago', value: line.concept + (line.meta ? ' · ' + line.meta : '') });
    details.push({ label: 'Importe', value: Utils.FormatMoney(line.amount) + ' €' });
    return details;
  }
}
